import { SstFilterValues } from '@features/ssts/hooks/useSstUrlState';
import Icon, { IconName } from '@features/ui-atoms/components/Icon';
import { Unit } from '@features/ui-form/components/FieldWrapper';
import { styled, useTheme } from '@mui/joy';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';
import DatePicker, { DateObject, type DatePickerRef, type Value } from 'react-multi-date-picker';

const zhMonths = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
const zhWeekDays = ['日', '一', '二', '三', '四', '五', '六'];

interface Props {
  filters: SstFilterValues;
  handleFilterChange: <K extends keyof SstFilterValues>(field: K, value: SstFilterValues[K]) => void;
}

export const SelectDateRangeFilter = ({ filters, handleFilterChange }: Props) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const pickerRef = useRef<DatePickerRef>(null);
  const { createdAtMin, createdAtMax } = filters;
  const value: Value[] | undefined =
    createdAtMin && createdAtMax ? [new DateObject(createdAtMin), new DateObject(createdAtMax)] : undefined;
  const toDateString = (d: DateObject) => d.format('YYYY-MM-DD');
  const onChange = (val: Value | Value[] | null) => {
    if (Array.isArray(val) && val[0] && val[1]) {
      const [from, to] = val as DateObject[];
      handleFilterChange('createdAtMin', toDateString(from));
      handleFilterChange('createdAtMax', toDateString(to));
    } else {
      handleFilterChange('createdAtMin', '');
      handleFilterChange('createdAtMax', '');
    }
  };

  return (
    <Container onClick={() => pickerRef.current?.openCalendar()}>
      <DatePicker
        ref={pickerRef}
        range
        value={value}
        onChange={onChange}
        format="YYYY-MM-DD"
        placeholder={t('action.filterDateRange')}
        portal
        inputClass="custom-date-input"
        months={i18n.language === 'zh' ? zhMonths : undefined}
        weekDays={i18n.language === 'zh' ? zhWeekDays : undefined}
        style={{
          boxSizing: 'border-box',
          padding: '4px 8px',
          marginRight: '0px',
          border: 'none',
          cursor: 'pointer',
          flex: 1,
          width: 190,
          height: 36,
          color: theme.palette.black,
          background: 'transparent',
        }}
      />
      <Unit>
        <Icon name={IconName.calendar} size={16} />
      </Unit>
    </Container>
  );
};

const Container = styled('div')(({ theme: { palette } }) => ({
  display: 'flex',
  width: 240,
  alignItems: 'stretch',
  height: 38,
  gap: 8,
  border: `1px solid ${palette.grey1}`,
  background: palette.white,

  '& .rmdp-container': {
    background: 'transparent',
  },

  '& .custom-date-input': {
    color: palette.black,
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    background: 'transparent',
  },

  '& .custom-date-input::placeholder': {
    color: palette.black,
    opacity: 1,
  },

  '& .custom-date-input:focus': {
    outline: 'none',
    boxShadow: 'none',
    border: 'none',
    background: 'transparent',
  },

  '&:hover': {
    borderColor: palette.grey2,
  },
}));
